import { useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Eye } from "lucide-react";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import type { MinisterInRequest } from "../types";
import { translateStatus, translateCredentialType } from "../utils/translations";

interface MinistersReportTableProps {
  data: MinisterInRequest[];
  isLoading?: boolean;
  onRowClick?: (row: MinisterInRequest) => void;
}

export function MinistersReportTable({
  data,
  isLoading = false,
  onRowClick,
}: MinistersReportTableProps) {
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showPosition, setShowPosition] = useState(false);
  const [showMinisterTerritory, setShowMinisterTerritory] = useState(false);

  const columns = useMemo<ColumnDef<MinisterInRequest>[]>(
    () => [
      {
        accessorKey: "minister_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Nombre Completo" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => (
          <div className="font-medium">{row.original.minister_name || "—"}</div>
        ),
      },
      {
        accessorKey: "dni",
        header: ({ column }) => <DataColumnHeader column={column} title="CI" />,
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.dni || "—",
      },
      {
        accessorKey: "adb_code",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Registro ADB" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.adb_code || "—",
      },
      {
        accessorKey: "degree_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Grado" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.degree_name || "—",
      },
      {
        accessorKey: "request_code",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Código Solicitud" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.request_code || "—",
      },
      {
        accessorKey: "credential_type",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Tipo Credencial" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => translateCredentialType(row.original.credential_type),
      },
      {
        accessorKey: "request_status",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Estado" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => {
          const status = row.original.request_status;
          return (
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(status)}`}
            >
              {translateStatus(status)}
            </span>
          );
        },
      },
      {
        accessorKey: "request_year",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Año" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.request_year || "—",
      },
      {
        accessorKey: "request_district_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Distrito" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.request_district_name || "—",
      },
      {
        accessorKey: "request_region_name",
        header: ({ column }) => (
          <DataColumnHeader column={column} title="Región" />
        ),
        filterFn: selectedIncludesFn,
        cell: ({ row }) => row.original.request_region_name || "—",
      },
      ...(showEmail
        ? [
            {
              accessorKey: "email",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Email" />
              ),
              filterFn: selectedIncludesFn,
              cell: ({ row }: { row: { original: MinisterInRequest } }) =>
                row.original.email || "—",
            } as ColumnDef<MinisterInRequest>,
          ]
        : []),
      ...(showPhone
        ? [
            {
              accessorKey: "cell_phone",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Celular" />
              ),
              filterFn: selectedIncludesFn,
              cell: ({ row }: { row: { original: MinisterInRequest } }) =>
                row.original.cell_phone || "—",
            } as ColumnDef<MinisterInRequest>,
          ]
        : []),
      ...(showPosition
        ? [
            {
              accessorKey: "position",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Posición" />
              ),
              filterFn: selectedIncludesFn,
              cell: ({ row }: { row: { original: MinisterInRequest } }) =>
                row.original.position || "—",
            } as ColumnDef<MinisterInRequest>,
          ]
        : []),
      ...(showMinisterTerritory
        ? [
            {
              accessorKey: "minister_district_name",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Distrito (Ministro)" />
              ),
              filterFn: selectedIncludesFn,
              cell: ({ row }: { row: { original: MinisterInRequest } }) =>
                row.original.minister_district_name || "—",
            } as ColumnDef<MinisterInRequest>,
            {
              accessorKey: "minister_region_name",
              header: ({ column }: { column: Parameters<typeof DataColumnHeader>[0]["column"] }) => (
                <DataColumnHeader column={column} title="Región (Ministro)" />
              ),
              filterFn: selectedIncludesFn,
              cell: ({ row }: { row: { original: MinisterInRequest } }) =>
                row.original.minister_region_name || "—",
            } as ColumnDef<MinisterInRequest>,
          ]
        : []),
    ],
    [showEmail, showPhone, showPosition, showMinisterTerritory]
  );

  const toolbarActions = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Eye className="mr-2 size-4" />
          Columnas
          <ChevronDown className="ml-2 size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuCheckboxItem checked={showEmail} onCheckedChange={setShowEmail}>
          Email
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={showPhone} onCheckedChange={setShowPhone}>
          Celular
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem checked={showPosition} onCheckedChange={setShowPosition}>
          Posición
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={showMinisterTerritory}
          onCheckedChange={setShowMinisterTerritory}
        >
          Territorio del Ministro
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <SelfContainedDataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      getSearchableText={(row) => {
        const adbCode = row.adb_code ?? "";
        const reqCode = row.request_code ?? "";
        return `${adbCode} ${adbCode.replace(/\D/g, "")} ${row.minister_name ?? ""} ${row.dni ?? ""} ${reqCode} ${reqCode.replace(/\D/g, "")}`;
      }}
      searchPlaceholder="Buscar por nombre, CI, código (ej: SOL26001)..."
      initialSorting={[{ id: "minister_name", desc: false }]}
      toolbarActions={toolbarActions}
      onRowClick={onRowClick}
      getRowClassName={onRowClick ? () => "cursor-pointer" : undefined}
      emptyMessage="No se encontraron ministros"
      pageSizeOptions={[20, 50, 100]}
    />
  );
}

function getStatusColor(status: string | null): string {
  switch (status) {
    case "DRAFT":
      return "bg-gray-100 text-gray-800";
    case "SUBMITTED":
      return "bg-blue-100 text-blue-800";
    case "APPROVED":
      return "bg-green-100 text-green-800";
    case "PRINTED":
      return "bg-purple-100 text-purple-800";
    case "DELIVERED":
      return "bg-emerald-100 text-emerald-800";
    case "REJECTED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}
